import type {
  RestoreVolumeToEbsSnapshotRequest,
  Configuration,
  ConvertResourceSnapshotsRequest,
  ConvertOtherSnapshotsOnDemandRequest,
} from '@repo/api-gw-sdk';
import {
  ConvertOtherSnapshotsOnDemandApi,
  ConvertResourceSnapshotsApi,
  ListAwsExternalRecoveryPointsApi,
  ListCloudSnapshotsApi,
  ListDailySnapshotsApi,
  SnapshotsApi,
} from '@repo/api-gw-sdk';

import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const baseUrl = '/convert';

export const convert = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const snapshotsApi = new SnapshotsApi(config);
  const convertResourceSnapshotsAPI = new ConvertResourceSnapshotsApi(config);
  const convertOtherSnapshotsOnDemandApi = new ConvertOtherSnapshotsOnDemandApi(
    config
  );
  const listCloudSnapshotsAPI = new ListCloudSnapshotsApi(config);
  const listDailySnapshotsAPI = new ListDailySnapshotsApi(config);
  const listAwsExternalRecoveryPointsAPI = new ListAwsExternalRecoveryPointsApi(
    config
  );

  return {
    volume: (
      resourceId: string,
      snapshotId: string,
      payload: RestoreVolumeToEbsSnapshotRequest
    ) =>
      snapshotsApi.restoreToEbsSnapshot(
        currentProjectId,
        resourceId,
        snapshotId,
        payload
      ),
    resourceSnapshots: (
      resourceId: string,
      convertResourceSnapshotsRequest: ConvertResourceSnapshotsRequest
    ) =>
      convertResourceSnapshotsAPI.convertResourceSnapshots(
        currentProjectId,
        resourceId,
        convertResourceSnapshotsRequest
      ),
    otherSnapshotOnDemand: (
      resourceId: string,
      convertOtherSnapshotsOnDemandRequest: ConvertOtherSnapshotsOnDemandRequest
    ) =>
      convertOtherSnapshotsOnDemandApi.convertOtherSnapshotsOnDemand(
        currentProjectId,
        resourceId,
        convertOtherSnapshotsOnDemandRequest
      ),
    useListCloudSnapshots: (resourceId: string, day: Date, enabled = true) => {
      const start = dayjs.utc(day).startOf('day').toDate();
      const end = dayjs.utc(day).endOf('day').toDate();

      return httpClient.executeWithConfig(
        listCloudSnapshotsAPI.listCloudSnapshots.bind(listCloudSnapshotsAPI),
        enabled ? baseUrl : null,
        '',
        // The drawer stays mounted across a day change and counts these points to pick a time slot;
        // the previous day's points would pick a slot that hides the new day's rows until this loads.
        { keepPreviousData: false },
        currentProjectId,
        resourceId,
        start,
        end
      );
    },
    /**
     * Range variant of useListCloudSnapshots — fetches all cloud snapshots in
     * an arbitrary [start, end] window. The Azure file-share storage-account
     * view uses this once per visible calendar month so the calendar dots and
     * the per-day drawer share a single network round-trip.
     */
    useListCloudSnapshotsRange: (
      resourceId: string | null,
      start: Date | undefined,
      end: Date | undefined
    ) => {
      const skip = !resourceId || !start || !end;
      return httpClient.executeWithConfig(
        listCloudSnapshotsAPI.listCloudSnapshots.bind(listCloudSnapshotsAPI),
        skip ? null : baseUrl,
        '',
        // The app-wide default keeps the previous key's data, which across a resource switch means
        // showing - and offering to restore from - another resource's recovery points until the new
        // request lands, or indefinitely if it fails. A stale point here names a real vault item, so
        // an empty tab is the only honest thing to render while this one loads.
        { keepPreviousData: false },
        currentProjectId,
        resourceId ?? '',
        start ?? new Date(0),
        end ?? new Date(0)
      );
    },
    useListDailySnapshots: (resourceId: string) => {
      return httpClient.execute(
        listDailySnapshotsAPI.listDailySnapshots.bind(listDailySnapshotsAPI),
        baseUrl,
        '',
        currentProjectId,
        resourceId
      );
    },
    useListAwsExternalRecoveryPoints: (
      resourceId: string | null,
      start: Date | undefined,
      end: Date | undefined
    ) => {
      const skip = !resourceId || !start || !end;
      return httpClient.executeWithConfig(
        listAwsExternalRecoveryPointsAPI.listAwsExternalRecoveryPoints.bind(
          listAwsExternalRecoveryPointsAPI
        ),
        skip ? null : baseUrl,
        '/aws-external-recovery-points',
        // A stale point would offer to restore another resource's recovery point while this one loads.
        { keepPreviousData: false },
        currentProjectId,
        resourceId ?? '',
        { start: start ?? new Date(0), end: end ?? new Date(0) }
      );
    },
  };
};
